/**
 * Production-safe logging utility
 * 
 * Usage:
 * import logger from './utils/logger';
 * 
 * logger.log('Debug message');
 * logger.error('Error message');
 * logger.warn('Warning message');
 * logger.info('Info message');
 */

const isDevelopment = process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test';

// Check if we're in production by looking at various environment indicators
const isProduction = process.env.NODE_ENV === 'production' || 
                     process.env.REACT_APP_ENV === 'production' ||
                     (typeof window !== 'undefined' && window.location.hostname !== 'localhost' && !window.location.hostname.includes('127.0.0.1'));

const isDebugEnabled = () => {
  try {
    return localStorage.getItem('debug_mode') === 'true';
  } catch {
    return false;
  }
};

class Logger {
  constructor() {
    this.updateEnabled();
  }

  updateEnabled() {
    // In production, only enable if debug mode is explicitly enabled via localStorage
    this.enabled = isProduction ? isDebugEnabled() : true;
  }

  log(...args) {
    this.updateEnabled();
    if (this.enabled) {
      console.log('[LOG]', ...args);
    }
  }

  error(...args) {
    this.updateEnabled();
    // In production, completely silence errors unless debug mode is enabled
    if (this.enabled && !isProduction) {
      console.error('[ERROR]', ...args);
    }
    
    // In production, send to error tracking service silently (no console output)
    if (isProduction && !this.enabled) {
      this.sendToErrorTracking('error', args);
    }
  }

  warn(...args) {
    this.updateEnabled();
    if (this.enabled) {
      console.warn('[WARN]', ...args);
    }
  }

  info(...args) {
    this.updateEnabled();
    if (this.enabled) {
      console.info('[INFO]', ...args);
    }
  }

  debug(...args) {
    this.updateEnabled();
    if (this.enabled && isDebugEnabled()) {
      console.debug('[DEBUG]', ...args);
    }
  }

  group(label) {
    this.updateEnabled();
    if (this.enabled) {
      console.group(label);
    }
  }

  groupEnd() {
    this.updateEnabled();
    if (this.enabled) {
      console.groupEnd();
    }
  }

  table(data) {
    this.updateEnabled();
    if (this.enabled) {
      console.table(data);
    }
  }

  time(label) {
    this.updateEnabled();
    if (this.enabled) {
      console.time(label);
    }
  }

  timeEnd(label) {
    this.updateEnabled();
    if (this.enabled) {
      console.timeEnd(label);
    }
  }

  sendToErrorTracking(level, args) {
    // Implement error tracking service integration here
    // Example: Sentry, LogRocket, etc.
    
    try {
      // Example structure for error tracking
      const errorData = {
        level,
        message: args.map(arg => 
          typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
        ).join(' '),
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href
      };

      // Send to your error tracking service
      // fetch('/api/log-error', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(errorData)
      // });
    } catch (e) {
      // Fail silently to avoid infinite loops
    }
  }

  // Enable debug mode in console (only accessible via window.logger.enableDebug())
  enableDebug() {
    try {
      localStorage.setItem('debug_mode', 'true');
      this.updateEnabled();
      if (this.enabled) {
        console.log('✅ Debug mode enabled - Console logs are now visible');
      }
    } catch (e) {
      // Silently fail if localStorage is not available
    }
  }

  // Disable debug mode
  disableDebug() {
    try {
      localStorage.removeItem('debug_mode');
      this.updateEnabled();
      console.log('🔇 Debug mode disabled - Console logs are now hidden');
    } catch (e) {
      // Silently fail if localStorage is not available
    }
  }
}

const logger = new Logger();

// Make it available globally for debugging
if (typeof window !== 'undefined') {
  window.logger = logger;
}

export default logger;
